<template>
  <div class="message-list flex flex-col h-full">
    <chat-header
      :otherUser="otherUser"
      :product="product"
      :showBackButton="showBackButton"
      @back="goBack"
      @openRatingModal="openRatingModal"
      @openBlockModal="openBlockModal"
    />

    <div
      ref="messagesContainer"
      class="flex-grow overflow-y-auto p-2 sm:p-4 space-y-3 sm:space-y-4"
      :class="{
        'flex items-center justify-center': showLoader || !hasMessages,
      }"
    >
      <div v-if="showLoader" class="text-center text-gray-400">
        <div class="flex items-center justify-center">
          <svg
            class="animate-spin h-5 w-5 mr-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Učitavanje...
        </div>
      </div>

      <div v-else-if="!hasMessages" class="text-center text-gray-400 px-4">
        <p>Nema poruka. Započnite razgovor.</p>
      </div>

      <template v-else>
        <message-item
          v-for="message in messages"
          :key="message.id"
          :message="message"
          :isCurrentUser="isCurrentUser(message)"
        />
      </template>
    </div>

    <div class="p-2 sm:p-4 bg-gray-700 border-t border-gray-600 flex-shrink-0">
      <message-input @send="sendMessage" @error="handleError" />
    </div>

    <!-- Modals -->
    <rating-modal
      v-if="ratingModal.show"
      :otherUser="otherUser"
      :ratingModal="ratingModal"
      @close="closeRatingModal"
      @setRatingLike="setRatingLike"
      @submit="submitRating"
    />

    <block-modal
      v-if="blockModal.show"
      :otherUser="otherUser"
      @close="closeBlockModal"
      @confirm="confirmBlock"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
  reactive,
} from 'vue';
import { useStore } from 'vuex';
import { Message } from '@/types/chat-types';
import MessageItem from './MessageItem.vue';
import MessageInput from './MessageInput.vue';
import ToastNotification from '@/components/ToastNotification.vue';
import ChatHeader from './ChatHeader.vue';
import RatingModal from './RatingModal.vue';
import BlockModal from './BlockModal.vue';
import api from '@/services/api';
import { debounce } from '../../utils/debounce';

export default defineComponent({
  name: 'MessageList',
  components: {
    MessageItem,
    MessageInput,
    ToastNotification,
    ChatHeader,
    RatingModal,
    BlockModal,
  },
  props: {
    showBackButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['back', 'error', 'notify'],
  setup(props, { emit }) {
    const store = useStore();
    const messagesContainer = ref<HTMLElement | null>(null);

    const messages = computed(() => store.getters['chat/currentMessages']);
    const currentConversation = computed(
      () => store.getters['chat/currentConversation']
    );
    const isLoading = computed(() => store.getters['chat/isLoading']);
    const currentUser = computed(() => store.getters['auth/currentUser']);
    const showLoader = ref(false);

    // Debounced loader functions
    const debouncedShowLoader = debounce(() => {
      showLoader.value = true;
    }, 300);

    const hideLoaderImmediately = () => {
      debouncedShowLoader.cancel();
      showLoader.value = false;
    };

    // Watch loading state
    watch(isLoading, (loading) => {
      if (loading) {
        debouncedShowLoader();
      } else {
        hideLoaderImmediately();
      }
    });

    // Modal za ocenjivanje
    const ratingModal = reactive({
      show: false,
      isLike: null as boolean | null,
      comment: '',
      ratingType: 'buyer' as 'buyer' | 'seller',
    });

    // Modal za blokiranje
    const blockModal = reactive({
      show: false,
    });

    const otherUser = computed(() => {
      // First, check if currentConversation has otherUser directly (for new conversations)
      if (currentConversation.value?.otherUser) {
        return currentConversation.value.otherUser;
      }

      // Otherwise fall back to the existing logic for conversations with messages
      if (currentConversation.value && messages.value.length) {
        const otherUserId = currentConversation.value.otherUserId;

        for (const message of messages.value) {
          if (message.senderId === otherUserId && message.sender) {
            return message.sender;
          }
          if (message.receiverId === otherUserId && message.receiver) {
            return message.receiver;
          }
        }
      }

      return null;
    });

    const product = computed(() => {
      // First, check if currentConversation has product directly (for new conversations)
      if (currentConversation.value?.product) {
        return currentConversation.value.product;
      }

      // Otherwise fall back to the existing logic for conversations with messages
      if (messages.value.length) {
        return messages.value[0].product;
      }

      return null;
    });

    const hasMessages = computed(() => messages.value.length > 0);

    const isCurrentUser = (message: Message) => {
      if (!currentUser.value) {
        return false;
      }
      return message.senderId === currentUser.value.id;
    };

    const scrollToBottom = () => {
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop =
            messagesContainer.value.scrollHeight;
        }
      });
    };

    const sendMessage = async (content: string) => {
      if (!currentConversation.value || !content.trim()) return;

      try {
        await store.dispatch('chat/sendMessage', {
          receiverId: currentConversation.value.otherUserId,
          productId: currentConversation.value.productId,
          content,
        });
        scrollToBottom();
      } catch (error) {
        console.error('Greška pri slanju poruke:', error);
        emit('error', error);
      }
    };

    // Funkcije za ocenjivanje korisnika
    const openRatingModal = () => {
      if (!otherUser.value || !product.value || !currentUser.value) {
        emit('error', {
          type: 'error',
          title: 'Greška',
          message: 'Nedostaju podaci o korisniku ili proizvodu.',
        });
        return;
      }

      const productOwnerId = product.value.userId;
      const isCurrentUserProductOwner = productOwnerId === currentUser.value.id;

      ratingModal.ratingType = isCurrentUserProductOwner ? 'buyer' : 'seller';
      ratingModal.show = true;
      ratingModal.isLike = null;
      ratingModal.comment = '';
    };

    const closeRatingModal = () => {
      ratingModal.show = false;
    };

    const setRatingLike = (isLike: boolean) => {
      ratingModal.isLike = isLike;
    };

    const submitRating = async () => {
      if (ratingModal.isLike === null) {
        emit('notify', {
          type: 'warning',
          title: 'Upozorenje',
          message: 'Morate odabrati pozitivnu ili negativnu ocenu.',
        });
        return;
      }

      if (
        ratingModal.comment.trim().length < 5 &&
        ratingModal.comment.trim().length > 0
      ) {
        emit('notify', {
          type: 'warning',
          title: 'Upozorenje',
          message: 'Komentar mora imati najmanje 5 karaktera.',
        });
        return;
      }

      try {
        if (!otherUser.value || !product.value) {
          throw new Error('Nedostaje korisnik ili proizvod');
        }

        await api.post('/api/ratings', {
          toUserId: otherUser.value.id,
          productId: product.value.id,
          isLike: ratingModal.isLike,
          comment: ratingModal.comment,
          ratingType: ratingModal.ratingType,
        });

        emit('notify', {
          type: 'success',
          title: 'Uspešno',
          message: 'Ocena je uspešno sačuvana. Hvala na povratnoj informaciji!',
        });

        closeRatingModal();
      } catch (error) {
        console.error('Greška pri ocenjivanju korisnika:', error);
        emit('error', error);
        closeRatingModal();
      }
    };

    // Funkcije za blokiranje korisnika
    const openBlockModal = () => {
      if (!otherUser.value) {
        emit('notify', {
          type: 'error',
          title: 'Greška',
          message: 'Nedostaju podaci o korisniku.',
        });
        return;
      }

      blockModal.show = true;
    };

    const closeBlockModal = () => {
      blockModal.show = false;
    };

    const confirmBlock = async () => {
      try {
        if (!otherUser.value) {
          throw new Error('Nedostaje korisnik');
        }

        await api.post(`/api/report-block/blocks/${otherUser.value.id}`);

        emit('notify', {
          type: 'success',
          title: 'Uspešno',
          message: 'Korisnik je uspešno blokiran.',
        });

        closeBlockModal();
        goBack();
      } catch (error) {
        console.error('Greška pri blokiranju korisnika:', error);
        emit('error', error);
        closeBlockModal();
      }
    };

    const handleError = (error: any) => {
      if (typeof error === 'object' && error !== null) {
        if (error.type && error.title && error.message) {
          emit('error', error);
        } else if (error.response && error.response.data) {
          const message = error.response.data.message || 'Došlo je do greške.';
          emit('error', { type: 'error', title: 'Greška', message });
        } else if (error.message) {
          emit('error', {
            type: 'error',
            title: 'Greška',
            message: error.message,
          });
        } else {
          emit('error', {
            type: 'error',
            title: 'Greška',
            message: 'Došlo je do nepoznate greške.',
          });
        }
      } else {
        emit('error', {
          type: 'error',
          title: 'Greška',
          message: 'Došlo je do nepoznate greške.',
        });
      }
    };

    const goBack = () => {
      emit('back');
    };

    watch(() => messages.value, scrollToBottom, { immediate: true });

    onMounted(() => {
      nextTick(() => {
        scrollToBottom();
      });
    });

    onBeforeUnmount(() => {
      debouncedShowLoader.cancel();
    });

    return {
      messages,
      isLoading,
      showLoader,
      otherUser,
      product,
      hasMessages,
      messagesContainer,
      isCurrentUser,
      sendMessage,
      handleError,
      goBack,
      // Rating funkcije
      ratingModal,
      openRatingModal,
      closeRatingModal,
      setRatingLike,
      submitRating,
      // Block funkcije
      blockModal,
      openBlockModal,
      closeBlockModal,
      confirmBlock,
    };
  },
});
</script>
